import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Redirect } from "react-router-dom";
import axios from "axios";
import {
  FaHashtag,
  FaSearch,
  FaSyncAlt,
  FaSave,
  FaSortNumericDown,
  FaSortAlphaDown,
  FaUserGraduate,
  FaIdCard,
  FaUndo,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import SelectClass from "../ChildComponent/SelectClass";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/admission-dashboard.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

function studentInitials(name) {
  const parts = String(name || "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0]?.[0] || "?").toUpperCase();
}

function getStaffId() {
  const staff = SessionManager.getStaffDetails();
  return staff?.staff_id || "";
}

const StudentAvatar = memo(function StudentAvatar({ name, imagePath }) {
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [imagePath]);

  const showImg = Boolean(imagePath) && !imgFailed;

  return (
    <div className="rn-row__avatar" aria-hidden>
      {showImg ? (
        <img src={imagePath} alt="" onError={() => setImgFailed(true)} />
      ) : (
        <span className="rn-row__initials">{studentInitials(name)}</span>
      )}
    </div>
  );
});

const StudentRollRow = memo(function StudentRollRow({
  student,
  draft,
  index,
  onChange,
}) {
  if (!draft) return null;

  const classLabel =
    student.section_id === "No"
      ? student.class_name
      : `${student.class_name || ""}:${student.section_name || ""}`;

  const rollDirty =
    String(draft.student_roll_no ?? "") !== String(student.student_roll_no ?? "");
  const admDirty =
    String(draft.sr_admission_no ?? "") !== String(student.sr_admission_no ?? "");

  return (
    <div
      className={`rn-row${rollDirty || admDirty ? " rn-row--dirty" : ""}`}
      style={{ "--rn-i": index }}
    >
      <div className="rn-row__who">
        <StudentAvatar
          name={student.stu_name}
          imagePath={student.student_img_path}
        />
        <div className="min-w-0">
          <div className="rn-row__name">{student.stu_name || "—"}</div>
          <div className="rn-row__chips">
            <span className="rn-chip rn-chip--father">
              {student.father_name ? `S/O ${student.father_name}` : "—"}
            </span>
            {classLabel ? (
              <span className="rn-chip rn-chip--class">{classLabel}</span>
            ) : null}
            <span className="rn-chip rn-chip--enroll">
              ID: {student.student_enrollment_no || "—"}
            </span>
          </div>
        </div>
      </div>

      <div className={`rn-row__field${rollDirty ? " rn-row__field--dirty" : ""}`}>
        <label className="rn-row__label" htmlFor={`roll-${draft.id}`}>
          Roll No
        </label>
        <div className="rn-input">
          <span className="rn-input__prefix" aria-hidden>
            #
          </span>
          <input
            id={`roll-${draft.id}`}
            type="text"
            className="rn-input__control"
            value={draft.student_roll_no ?? ""}
            onChange={(e) =>
              onChange(draft.id, "student_roll_no", e.target.value)
            }
            placeholder="—"
            inputMode="numeric"
          />
        </div>
      </div>

      <div className={`rn-row__field${admDirty ? " rn-row__field--dirty" : ""}`}>
        <label className="rn-row__label" htmlFor={`adm-${draft.id}`}>
          Admission No
        </label>
        <div className="rn-input">
          <span className="rn-input__prefix rn-input__prefix--adm" aria-hidden>
            <FaIdCard />
          </span>
          <input
            id={`adm-${draft.id}`}
            type="text"
            className="rn-input__control"
            value={draft.sr_admission_no ?? ""}
            onChange={(e) =>
              onChange(draft.id, "sr_admission_no", e.target.value)
            }
            placeholder="—"
          />
        </div>
      </div>
    </div>
  );
});

const RollNumberManagement = () => {
  const loggedIn = localStorage.getItem("token") != null;

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadClass, setLoadClass] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [selectedClassPosition, setSelectedClassPosition] = useState(0);
  const [classSectionName, setClassSectionName] = useState("");
  const [students, setStudents] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [rollBackup, setRollBackup] = useState(null);
  const [restoreUi, setRestoreUi] = useState({
    open: false,
    phase: "confirm", // confirm | running | done | error
    step: 0,
    message: "",
    backup: null,
  });
  const [rollOutConfirm, setRollOutConfirm] = useState({
    open: false,
    mode: null, // from1 | alpha
  });

  useEffect(() => {
    setLoadClass(true);
  }, []);

  const loadRollBackup = useCallback(async () => {
    if (!classId || !sectionId || !sessionId) {
      setRollBackup(null);
      return;
    }
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      setRollBackup(null);
      return;
    }
    try {
      const res = await axios.get(`${API_BASE()}getClassRollNumberBackup.php`, {
        params: {
          school_code: schoolCode,
          class_id: classId,
          section_id: sectionId,
          session_id: sessionId,
        },
      });
      if (res?.data?.api_status === "OK" && res.data.data) {
        setRollBackup(res.data.data);
      } else {
        setRollBackup(null);
      }
    } catch (error) {
      console.warn("[RollNumberManagement] backup check failed", error);
      setRollBackup(null);
    }
  }, [classId, sectionId, sessionId]);

  const loadStudents = useCallback(
    async ({ soft = false } = {}) => {
      if (!classId || !sectionId || !sessionId) {
        setStudents([]);
        setDrafts([]);
        setRollBackup(null);
        return;
      }

      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) {
        myToast.notify_warning("School code is missing.");
        return;
      }

      if (soft) setRefreshing(true);
      else setLoading(true);

      try {
        const params = {
          school_code: schoolCode,
          class_id: classId,
          section_id: sectionId,
          session_id: sessionId,
        };

        let payload = null;
        try {
          const res = await axios.get(
            `${API_BASE()}getCurrentStudentForManageRecords.php`,
            { params }
          );
          payload = res?.data;
        } catch (primaryError) {
          console.warn(
            "[RollNumberManagement] manage-records endpoint failed, falling back",
            primaryError
          );
        }

        if (!payload || payload.api_status !== "OK") {
          const res = await axios.get(`${API_BASE()}getCurrentStudent.php`, {
            params,
          });
          payload = res?.data;
        }

        if (payload?.api_status === "OK" && Array.isArray(payload.data)) {
          setStudents(payload.data);
          setDrafts(
            payload.data.map((item) => ({
              id: item.id,
              student_enrollment_no: item.student_enrollment_no,
              student_roll_no: item.student_roll_no ?? "",
              sr_admission_no: item.sr_admission_no ?? "",
            }))
          );
        } else {
          setStudents([]);
          setDrafts([]);
        }
        await loadRollBackup();
      } catch (error) {
        console.error("[RollNumberManagement] load error", error);
        setStudents([]);
        setDrafts([]);
        myToast.notify_warning("Failed to load students.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [classId, sectionId, sessionId, loadRollBackup]
  );

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  const onClassSelect = useCallback(
    (nextClassId, nextSectionId, nextSessionId, position, className, sectionName) => {
      setSectionId(nextSectionId);
      setSessionId(nextSessionId);
      setClassSectionName(
        nextSectionId === "No" ? className : `${className}:${sectionName}`
      );
      setSelectedClassPosition(position);
      setClassId(nextClassId);
      setSearchTerm("");
    },
    []
  );

  const handleDraftChange = useCallback((id, key, value) => {
    setDrafts((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [key]: value } : row))
    );
  }, []);

  const dirtyChanges = useMemo(() => {
    const draftMap = new Map(drafts.map((d) => [d.id, d]));
    const changes = [];
    students.forEach((item) => {
      const draft = draftMap.get(item.id);
      if (!draft) return;
      const rollChanged =
        String(draft.student_roll_no ?? "") !== String(item.student_roll_no ?? "");
      const admChanged =
        String(draft.sr_admission_no ?? "") !== String(item.sr_admission_no ?? "");
      if (rollChanged || admChanged) {
        changes.push({
          student_enrollment_no: item.student_enrollment_no,
          stu_name: item.stu_name,
          student_roll_no: item.student_roll_no,
          new_roll_no: draft.student_roll_no,
          sr_admission_no: item.sr_admission_no,
          new_admission_no: draft.sr_admission_no,
        });
      }
    });
    return changes;
  }, [students, drafts]);

  const dirtyCount = dirtyChanges.length;

  const draftById = useMemo(() => {
    const map = new Map();
    drafts.forEach((d) => map.set(d.id, d));
    return map;
  }, [drafts]);

  const filteredStudents = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) => {
      const draft = draftById.get(s.id);
      const hay = [
        s.stu_name,
        s.father_name,
        s.student_enrollment_no,
        s.student_roll_no,
        s.sr_admission_no,
        draft?.student_roll_no,
        draft?.sr_admission_no,
      ]
        .map((v) => String(v ?? "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [students, draftById, searchTerm]);

  const submitAllChanges = useCallback(async () => {
    if (!dirtyChanges.length) {
      myToast.notify_warning("No changes to submit.");
      return;
    }

    const staffId = getStaffId();
    const schoolCode = SessionManager.getSchoolCode();
    if (!staffId || !schoolCode) {
      myToast.notify_warning("Staff or school code is missing.");
      return;
    }
    if (!classId || !sectionId || !sessionId) {
      myToast.notify_warning("Please select a class first.");
      return;
    }

    setSubmitting(true);
    try {
      const requestPayload = {
        staff_id: String(staffId),
        school_code: String(schoolCode),
        class_id: String(classId),
        section_id: String(sectionId),
        session_id: String(sessionId),
        student_list: JSON.stringify(dirtyChanges),
      };

      let payload = null;
      try {
        const body = new URLSearchParams(requestPayload);
        const res = await axios.post(
          `${API_BASE()}updateClassRollNumberAndAdmNo.php`,
          body,
          { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
        );
        payload = res?.data;
      } catch (postError) {
        console.warn(
          "[RollNumberManagement] Submit All POST failed, trying GET",
          postError
        );
        const res = await axios.get(
          `${API_BASE()}updateClassRollNumberAndAdmNo.php`,
          { params: requestPayload }
        );
        payload = res?.data;
      }

      if (payload?.api_status === "OK") {
        myToast.notify_success(
          payload.api_remark || "All changes submitted successfully."
        );
        await loadStudents({ soft: true });
      } else {
        myToast.notify_warning(payload?.api_remark || "Bulk update failed.");
      }
    } catch (error) {
      console.error("[RollNumberManagement] submitAll error", error);
      myToast.notify_warning("Failed to submit changes.");
    } finally {
      setSubmitting(false);
    }
  }, [
    dirtyChanges,
    classId,
    sectionId,
    sessionId,
    loadStudents,
  ]);

  const openRollOutConfirm = useCallback(
    (mode) => {
      if (!students.length) {
        myToast.notify_warning("No students to roll out.");
        return;
      }
      setRollOutConfirm({ open: true, mode });
    },
    [students.length]
  );

  const closeRollOutConfirm = useCallback(() => {
    if (submitting) return;
    setRollOutConfirm({ open: false, mode: null });
  }, [submitting]);

  const applyRollOut = useCallback(
    async (mode) => {
      if (!students.length) {
        myToast.notify_warning("No students to roll out.");
        return;
      }

      const staffId = getStaffId();
      const schoolCode = SessionManager.getSchoolCode();
      if (!staffId || !schoolCode) {
        myToast.notify_warning("Staff or school code is missing.");
        return;
      }

      const sorted = [...students].sort((a, b) => {
        if (mode === "alpha") {
          return String(a.stu_name || "").localeCompare(
            String(b.stu_name || ""),
            undefined,
            { sensitivity: "base" }
          );
        }
        return String(a.student_enrollment_no || "").localeCompare(
          String(b.student_enrollment_no || ""),
          undefined,
          { numeric: true, sensitivity: "base" }
        );
      });

      const payloadList = sorted.map((item, i) => ({
        student_enrollment_no: item.student_enrollment_no,
        stu_name: item.stu_name,
        student_roll_no: item.student_roll_no,
        new_roll_no: String(i + 1),
      }));

      setSubmitting(true);
      try {
        const body = new URLSearchParams({
          staff_id: String(staffId),
          school_code: String(schoolCode),
          class_id: String(classId),
          section_id: String(sectionId),
          session_id: String(sessionId),
          student_list: JSON.stringify(payloadList),
          roll_out_mode: mode === "alpha" ? "alphabetically" : "from_1",
        });
        const res = await axios.post(
          `${API_BASE()}updateClassRollNumber.php`,
          body,
          { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
        );
        const payload = res?.data;
        if (payload?.api_status === "OK") {
          if (payload.backup) {
            setRollBackup(payload.backup);
          }
          setRollOutConfirm({ open: false, mode: null });
          myToast.notify_success(
            payload.api_remark ||
              (mode === "alpha"
                ? "Roll numbers assigned alphabetically. Previous rolls saved."
                : "Roll numbers assigned from 1. Previous rolls saved.")
          );
          await loadStudents({ soft: true });
        } else {
          myToast.notify_warning(payload?.api_remark || "Roll out failed.");
        }
      } catch (error) {
        console.error("[RollNumberManagement] rollOut error", error);
        myToast.notify_warning("Failed to roll out numbers.");
      } finally {
        setSubmitting(false);
      }
    },
    [students, classId, sectionId, sessionId, loadStudents]
  );

  const confirmRollOut = useCallback(() => {
    if (!rollOutConfirm.mode) return;
    applyRollOut(rollOutConfirm.mode);
  }, [rollOutConfirm.mode, applyRollOut]);

  const openRestoreProcess = useCallback(() => {
    if (!rollBackup?.backup_id) {
      myToast.notify_warning("No previous roll backup found.");
      return;
    }
    setRestoreUi({
      open: true,
      phase: "confirm",
      step: 0,
      message: "",
      backup: rollBackup,
    });
  }, [rollBackup]);

  const closeRestoreProcess = useCallback(() => {
    if (restoreUi.phase === "running") return;
    setRestoreUi({
      open: false,
      phase: "confirm",
      step: 0,
      message: "",
      backup: null,
    });
  }, [restoreUi.phase]);

  const restorePreviousRolls = useCallback(async () => {
    if (!rollBackup?.backup_id) {
      myToast.notify_warning("No previous roll backup found.");
      return;
    }

    const staffId = getStaffId();
    const schoolCode = SessionManager.getSchoolCode();
    if (!staffId || !schoolCode) {
      myToast.notify_warning("Staff or school code is missing.");
      return;
    }
    if (!classId || !sectionId || !sessionId) {
      myToast.notify_warning("Please select a class first.");
      return;
    }

    setSubmitting(true);
    setRestoreUi({
      open: true,
      phase: "running",
      step: 1,
      message: "Preparing restore…",
      backup: rollBackup,
    });

    try {
      setRestoreUi((prev) => ({
        ...prev,
        step: 2,
        message: "Restoring previous roll numbers…",
      }));

      const body = new URLSearchParams({
        staff_id: String(staffId),
        school_code: String(schoolCode),
        class_id: String(classId),
        section_id: String(sectionId),
        session_id: String(sessionId),
        backup_id: String(rollBackup.backup_id),
      });
      const res = await axios.post(
        `${API_BASE()}restoreClassRollNumberBackup.php`,
        body,
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
      );
      const payload = res?.data;
      if (payload?.api_status === "OK") {
        setRestoreUi({
          open: true,
          phase: "running",
          step: 3,
          message: "Reloading student list…",
          backup: rollBackup,
        });
        setRollBackup(null);
        await loadStudents({ soft: true });
        setRestoreUi({
          open: true,
          phase: "done",
          step: 4,
          message:
            payload.api_remark || "Previous roll numbers restored successfully.",
          backup: rollBackup,
        });
        myToast.notify_success(
          payload.api_remark || "Previous roll numbers restored."
        );
      } else {
        const remark =
          payload?.api_remark || "Unable to restore previous rolls.";
        setRestoreUi({
          open: true,
          phase: "error",
          step: 2,
          message: remark,
          backup: rollBackup,
        });
        myToast.notify_warning(remark);
      }
    } catch (error) {
      console.error("[RollNumberManagement] restore error", error);
      setRestoreUi({
        open: true,
        phase: "error",
        step: 2,
        message: "Failed to restore previous rolls. Please try again.",
        backup: rollBackup,
      });
      myToast.notify_warning("Failed to restore previous rolls.");
    } finally {
      setSubmitting(false);
    }
  }, [rollBackup, classId, sectionId, sessionId, loadStudents]);

  if (!loggedIn) return <Redirect to="/Login" />;

  const classSelected = Boolean(classId && sectionId && sessionId);
  const busy = loading || refreshing || submitting;

  const restoreSteps = [
    { id: 1, label: "Prepare restore" },
    { id: 2, label: "Apply previous roll numbers" },
    { id: 3, label: "Reload student list" },
    { id: 4, label: "Complete" },
  ];

  const backupInfo = restoreUi.backup || rollBackup;
  const backupModeLabel =
    backupInfo?.roll_out_mode === "alphabetically"
      ? "Roll Out Alphabetically"
      : backupInfo?.roll_out_mode === "from_1"
      ? "Roll Out From 1"
      : backupInfo?.roll_out_mode || "Last Roll Out";

  const backupDateLabel = backupInfo?.date_added
    ? new Date(backupInfo.date_added).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

  return (
    <div
      className="content payroll-page sm-page rn-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Roll Number Management</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaHashtag />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Roll Number Management</h1>
              <p className="sm-hero__sub">
                {classSectionName
                  ? `${classSectionName} · ${students.length} student${
                      students.length === 1 ? "" : "s"
                    }`
                  : "Select a class to manage roll & admission numbers"}
                {dirtyCount > 0 ? ` · ${dirtyCount} unsaved` : ""}
              </p>
            </div>
          </div>

          <div className="sm-hero__tools">
            <div className="sm-hero__search-group">
              <FaSearch className="rn-search-icon" aria-hidden />
              <input
                type="search"
                className="sm-hero__search"
                placeholder="Search students…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search students"
                disabled={!classSelected}
              />
            </div>
            <button
              type="button"
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              onClick={() => loadStudents({ soft: true })}
              disabled={busy || !classSelected}
              aria-label="Refresh list"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="rn-content">
          {loadClass ? (
            <div className="rn-class-wrap">
              <SelectClass
                value={loadClass}
                action={onClassSelect}
                selected_position={selectedClassPosition}
              />
            </div>
          ) : null}

          {!classSelected ? (
            <div className="sm-empty">
              <FaUserGraduate className="rn-empty-icon" aria-hidden />
              <p className="sm-empty__title">Select a class</p>
              <p className="sm-empty__hint">
                Choose class and section to load students
              </p>
            </div>
          ) : loading ? (
            <div className="rn-loading">
              <MyLoader />
            </div>
          ) : (
            <>
              {(() => {
                const rollOutButtons = (
                  <div className="rn-toolbar__actions">
                    <button
                      type="button"
                      className="rn-btn rn-btn--roll1"
                      onClick={() => openRollOutConfirm("from1")}
                      disabled={busy || !students.length}
                      title="Assign roll 1, 2, 3… by enrollment no"
                    >
                      <span className="rn-btn__icon" aria-hidden>
                        <FaSortNumericDown />
                      </span>
                      <span className="rn-btn__text">
                        <span className="rn-btn__title">Roll Out From 1</span>
                        <span className="rn-btn__hint">By enrollment</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      className="rn-btn rn-btn--alpha"
                      onClick={() => openRollOutConfirm("alpha")}
                      disabled={busy || !students.length}
                      title="Assign roll 1, 2, 3… alphabetically by name"
                    >
                      <span className="rn-btn__icon" aria-hidden>
                        <FaSortAlphaDown />
                      </span>
                      <span className="rn-btn__text">
                        <span className="rn-btn__title">Roll Out Alphabetically</span>
                        <span className="rn-btn__hint">By student name</span>
                      </span>
                    </button>
                    {rollBackup?.backup_id ? (
                      <button
                        type="button"
                        className="rn-btn rn-btn--restore"
                        onClick={openRestoreProcess}
                        disabled={busy}
                        title="Restore previous roll numbers from last Roll Out"
                      >
                        <span className="rn-btn__icon" aria-hidden>
                          <FaUndo />
                        </span>
                        <span className="rn-btn__text">
                          <span className="rn-btn__title">Restore Previous</span>
                          <span className="rn-btn__hint">
                            {rollBackup.student_count
                              ? `${rollBackup.student_count} students`
                              : "Last backup"}
                          </span>
                        </span>
                      </button>
                    ) : null}
                  </div>
                );

                const submitButton = (
                  <button
                    type="button"
                    className="rn-btn rn-btn--solid"
                    onClick={submitAllChanges}
                    disabled={busy || dirtyCount === 0}
                  >
                    <span className="rn-btn__icon" aria-hidden>
                      <FaSave />
                    </span>
                    <span className="rn-btn__text">
                      <span className="rn-btn__title">
                        {submitting
                          ? "Saving…"
                          : dirtyCount > 0
                          ? `Submit All (${dirtyCount})`
                          : "Submit All"}
                      </span>
                      <span className="rn-btn__hint">
                        {dirtyCount > 0 ? "Save pending edits" : "No changes yet"}
                      </span>
                    </span>
                  </button>
                );

                return (
                  <>
                    <div className="rn-toolbar rn-toolbar--top">
                      <div className="rn-toolbar__status" aria-live="polite">
                        <span className="rn-status-chip rn-status-chip--students">
                          {students.length} student
                          {students.length === 1 ? "" : "s"}
                        </span>
                        {dirtyCount > 0 ? (
                          <span className="rn-status-chip rn-status-chip--unsaved">
                            {dirtyCount} unsaved
                          </span>
                        ) : (
                          <span className="rn-status-chip rn-status-chip--clean">
                            All saved
                          </span>
                        )}
                      </div>
                      {submitButton}
                    </div>

                    {filteredStudents.length === 0 ? (
                      <div className="sm-empty">
                        <p className="sm-empty__title">No students found</p>
                        <p className="sm-empty__hint">
                          {searchTerm
                            ? "Try a different search term."
                            : "This class has no active students."}
                        </p>
                      </div>
                    ) : (
                      <div className="rn-list">
                        {filteredStudents.map((student, index) => (
                          <StudentRollRow
                            key={student.id || student.student_enrollment_no}
                            student={student}
                            draft={draftById.get(student.id)}
                            index={index}
                            onChange={handleDraftChange}
                          />
                        ))}
                      </div>
                    )}

                    {students.length > 0 ? (
                      <div className="rn-toolbar rn-toolbar--bottom">
                        {rollOutButtons}
                        {submitButton}
                      </div>
                    ) : null}
                  </>
                );
              })()}
            </>
          )}
        </div>
      </div>

      {rollOutConfirm.open ? (
        <div className="rn-restore-overlay" role="dialog" aria-modal="true">
          <div className="rn-restore-panel rn-restore-panel--rollout">
            <div className="rn-restore-panel__head">
              <div
                className={`rn-restore-panel__icon${
                  rollOutConfirm.mode === "alpha"
                    ? " rn-restore-panel__icon--alpha"
                    : " rn-restore-panel__icon--roll1"
                }`}
                aria-hidden
              >
                {rollOutConfirm.mode === "alpha" ? (
                  <FaSortAlphaDown />
                ) : (
                  <FaSortNumericDown />
                )}
              </div>
              <div className="min-w-0">
                <h3 className="rn-restore-panel__title">
                  {rollOutConfirm.mode === "alpha"
                    ? "Roll Out Alphabetically?"
                    : "Roll Out From 1?"}
                </h3>
                <p className="rn-restore-panel__sub">
                  {rollOutConfirm.mode === "alpha"
                    ? "Students will be numbered 1, 2, 3… by name (A–Z)."
                    : "Students will be numbered 1, 2, 3… by enrollment ID."}
                </p>
              </div>
            </div>

            <div className="rn-restore-meta">
              <div className="rn-restore-meta__item">
                <span className="rn-restore-meta__label">Class</span>
                <span className="rn-restore-meta__value">
                  {classSectionName || "—"}
                </span>
              </div>
              <div className="rn-restore-meta__item">
                <span className="rn-restore-meta__label">Students</span>
                <span className="rn-restore-meta__value">{students.length}</span>
              </div>
              <div className="rn-restore-meta__item">
                <span className="rn-restore-meta__label">Order</span>
                <span className="rn-restore-meta__value">
                  {rollOutConfirm.mode === "alpha"
                    ? "By student name"
                    : "By enrollment no"}
                </span>
              </div>
              <div className="rn-restore-meta__item">
                <span className="rn-restore-meta__label">Backup</span>
                <span className="rn-restore-meta__value">
                  Current rolls will be saved
                </span>
              </div>
            </div>

            <div className="rn-restore-warn">
              <FaExclamationTriangle aria-hidden />
              <span>
                Current roll numbers will be replaced. You can use Restore
                Previous later to undo this Roll Out.
              </span>
            </div>

            <div className="rn-restore-actions">
              <button
                type="button"
                className="rn-btn rn-btn--ghost"
                onClick={closeRollOutConfirm}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`rn-btn${
                  rollOutConfirm.mode === "alpha"
                    ? " rn-btn--alpha"
                    : " rn-btn--roll1"
                }`}
                onClick={confirmRollOut}
                disabled={submitting}
              >
                <span className="rn-btn__icon" aria-hidden>
                  {rollOutConfirm.mode === "alpha" ? (
                    <FaSortAlphaDown />
                  ) : (
                    <FaSortNumericDown />
                  )}
                </span>
                <span className="rn-btn__text">
                  <span className="rn-btn__title">
                    {submitting ? "Rolling out…" : "Confirm Roll Out"}
                  </span>
                  <span className="rn-btn__hint">
                    {students.length} student
                    {students.length === 1 ? "" : "s"}
                  </span>
                </span>
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {restoreUi.open ? (
        <div className="rn-restore-overlay" role="dialog" aria-modal="true">
          <div className="rn-restore-panel">
            <div className="rn-restore-panel__head">
              <div className="rn-restore-panel__icon" aria-hidden>
                {restoreUi.phase === "done" ? (
                  <FaCheckCircle />
                ) : restoreUi.phase === "error" ? (
                  <FaTimesCircle />
                ) : (
                  <FaUndo />
                )}
              </div>
              <div className="min-w-0">
                <h3 className="rn-restore-panel__title">
                  {restoreUi.phase === "confirm"
                    ? "Restore previous rolls?"
                    : restoreUi.phase === "running"
                    ? "Restoring rolls…"
                    : restoreUi.phase === "done"
                    ? "Restore complete"
                    : "Restore failed"}
                </h3>
                <p className="rn-restore-panel__sub">
                  {restoreUi.phase === "confirm"
                    ? "This will replace current roll numbers with the saved backup."
                    : restoreUi.message}
                </p>
              </div>
            </div>

            {backupInfo ? (
              <div className="rn-restore-meta">
                <div className="rn-restore-meta__item">
                  <span className="rn-restore-meta__label">Backup</span>
                  <span className="rn-restore-meta__value">
                    {backupInfo.backup_id}
                  </span>
                </div>
                <div className="rn-restore-meta__item">
                  <span className="rn-restore-meta__label">From</span>
                  <span className="rn-restore-meta__value">{backupModeLabel}</span>
                </div>
                <div className="rn-restore-meta__item">
                  <span className="rn-restore-meta__label">Students</span>
                  <span className="rn-restore-meta__value">
                    {backupInfo.student_count || "—"}
                  </span>
                </div>
                <div className="rn-restore-meta__item">
                  <span className="rn-restore-meta__label">Saved on</span>
                  <span className="rn-restore-meta__value">{backupDateLabel}</span>
                </div>
              </div>
            ) : null}

            {restoreUi.phase === "confirm" ? (
              <div className="rn-restore-warn">
                <FaExclamationTriangle aria-hidden />
                <span>
                  Current roll numbers will be overwritten. You can roll out again
                  later if needed.
                </span>
              </div>
            ) : null}

            <ol className="rn-restore-steps">
              {restoreSteps.map((step) => {
                const active = restoreUi.step === step.id;
                const done =
                  restoreUi.phase === "done" || restoreUi.step > step.id;
                const failed =
                  restoreUi.phase === "error" && restoreUi.step === step.id;
                return (
                  <li
                    key={step.id}
                    className={`rn-restore-step${
                      done ? " rn-restore-step--done" : ""
                    }${active ? " rn-restore-step--active" : ""}${
                      failed ? " rn-restore-step--error" : ""
                    }`}
                  >
                    <span className="rn-restore-step__index" aria-hidden>
                      {done && !failed ? <FaCheckCircle /> : step.id}
                    </span>
                    <span className="rn-restore-step__label">{step.label}</span>
                    {active && restoreUi.phase === "running" ? (
                      <span className="rn-restore-step__pulse" aria-hidden />
                    ) : null}
                  </li>
                );
              })}
            </ol>

            <div className="rn-restore-actions">
              {restoreUi.phase === "confirm" ? (
                <>
                  <button
                    type="button"
                    className="rn-btn rn-btn--ghost"
                    onClick={closeRestoreProcess}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="rn-btn rn-btn--restore"
                    onClick={restorePreviousRolls}
                    disabled={busy}
                  >
                    <span className="rn-btn__icon" aria-hidden>
                      <FaUndo />
                    </span>
                    <span className="rn-btn__text">
                      <span className="rn-btn__title">Start Restore</span>
                      <span className="rn-btn__hint">Apply backup now</span>
                    </span>
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="rn-btn rn-btn--solid"
                  onClick={closeRestoreProcess}
                  disabled={restoreUi.phase === "running"}
                >
                  <span className="rn-btn__text">
                    <span className="rn-btn__title">
                      {restoreUi.phase === "running" ? "Please wait…" : "Close"}
                    </span>
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default RollNumberManagement;
